import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '../contexts/SubscriptionContext';
import PlanSelectionModal from './PlanSelectionModal';
import { getPremiumTool } from '../config/premiumTools';
import { Lock, Crown, Check, ListChecks, Lightbulb, FileText } from 'lucide-react';

// Optional, non-blocking analytics. Uses an existing provider if present
// (gtag / posthog); otherwise a safe no-op. Never a prerequisite for the feature.
const trackEvent = (name, props = {}) => {
  try {
    if (typeof window === 'undefined') return;
    if (typeof window.gtag === 'function') window.gtag('event', name, props);
    else if (window.posthog?.capture) window.posthog.capture(name, props);
  } catch (_) { /* analytics must never break the UI */ }
};

const PremiumToolPreview = ({ toolId }) => {
  const { plans } = useSubscription();
  const [open, setOpen] = useState(false);
  const tool = getPremiumTool(toolId);

  const monthly = plans.find((p) => p.plan_type === 'monthly');
  const annual = plans.find((p) => p.plan_type === 'annual');

  useEffect(() => {
    if (tool) trackEvent('premium_tool_preview_opened', { tool_id: toolId });
  }, [toolId, tool]);

  // Fallback for any tool without metadata — keeps the gate functional.
  if (!tool) {
    return (
      <div className="flex items-center justify-center py-12 px-4" data-testid="premium-tool-preview">
        <div className="max-w-lg w-full text-center rounded-2xl border border-amber-500/30 bg-gradient-to-b from-slate-800 to-slate-900 p-8 shadow-2xl">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-400/15 mb-4">
            <Lock className="h-7 w-7 text-amber-400" />
          </div>
          <h2 className="text-xl font-extrabold text-white">This tool requires Option Wizard Premium</h2>
          <Button
            onClick={() => { trackEvent('premium_tool_unlock_clicked', { tool_id: toolId }); setOpen(true); }}
            className="mt-6 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-900 font-bold px-6 h-11"
            data-testid="tool-unlock-btn"
          >
            <Crown className="h-4 w-4 mr-2" /> Unlock with Premium
          </Button>
        </div>
        <PlanSelectionModal open={open} onOpenChange={setOpen} />
      </div>
    );
  }

  const { Icon, name, shortDescription, whatItDoes, whyItMatters, example, outputDescription } = tool;
  const previewTitleId = `premium-tool-title-${toolId}`;

  const openPlans = (source) => {
    trackEvent(source === 'compare' ? 'premium_tool_plan_selection_opened' : 'premium_tool_unlock_clicked', { tool_id: toolId });
    setOpen(true);
  };

  return (
    <>
      <section
        className="mx-auto max-w-3xl"
        role="region"
        aria-labelledby={previewTitleId}
        data-testid="premium-tool-preview"
        data-tool-id={toolId}
      >
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-b from-slate-800 to-slate-900 shadow-2xl overflow-hidden">
          {/* Header / Level 1 — quick value */}
          <div className="p-6 sm:p-8 border-b border-slate-700/60">
            <div className="flex items-start gap-4">
              <div className="shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-400/15 border border-amber-400/20">
                <Icon className="h-7 w-7 text-amber-400" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-300/90 bg-amber-400/10 px-2.5 py-0.5 rounded-full mb-2">
                  <Lock className="h-3 w-3" aria-hidden="true" /> Premium Tool
                </div>
                <h2 id={previewTitleId} className="text-2xl font-extrabold text-white leading-tight" data-testid="tool-preview-name">
                  {name}
                </h2>
                <p className="text-slate-300 mt-2 text-sm sm:text-base" data-testid="tool-preview-short">
                  {shortDescription}
                </p>
              </div>
            </div>
          </div>

          {/* Body — Level 2 (what it does) + Level 3 (why) + Example */}
          <div className="p-6 sm:p-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-bold text-white uppercase tracking-wide">
                <ListChecks className="h-4 w-4 text-amber-400" aria-hidden="true" /> What it does
              </h3>
              <ul className="mt-3 space-y-2" data-testid="tool-preview-capabilities">
                {whatItDoes.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-5">
              <div>
                <h3 className="flex items-center gap-2 text-sm font-bold text-white uppercase tracking-wide">
                  <Lightbulb className="h-4 w-4 text-amber-400" aria-hidden="true" /> Why use it?
                </h3>
                <p className="mt-2 text-sm text-slate-300" data-testid="tool-preview-why">{whyItMatters}</p>
              </div>
              <div className="rounded-xl bg-slate-950/40 border border-slate-700/60 p-4">
                <h3 className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wide">
                  <FileText className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" /> Example
                </h3>
                <p className="mt-1.5 text-sm text-slate-300" data-testid="tool-preview-example">{example}</p>
                <p className="mt-3 text-xs text-slate-500">
                  <span className="font-semibold text-slate-400">You’ll get: </span>{outputDescription}
                </p>
              </div>
            </div>
          </div>

          {/* CTA — pricing from authoritative subscription plans (never hardcoded) */}
          <div className="p-6 sm:p-8 bg-slate-950/40 border-t border-slate-700/60">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <p className="text-sm text-slate-300 font-semibold">Unlock {name} with Option Wizard Premium</p>
                <div className="flex items-end flex-wrap gap-x-2 gap-y-1 mt-1">
                  <span className="text-3xl font-extrabold text-amber-400">${monthly?.price ?? '19.99'}</span>
                  <span className="text-slate-400 mb-1">/mo</span>
                  <span className="text-slate-500 ml-1">or ${annual?.price ?? '99.99'}/yr</span>
                </div>
                {(monthly?.trial_days || annual?.trial_days) ? (
                  <p className="text-xs text-amber-400/80 mt-1">
                    Includes a {monthly?.trial_days || annual?.trial_days}-day free trial
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button
                  onClick={() => openPlans('unlock')}
                  className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-900 font-bold px-6 h-11"
                  data-testid="tool-unlock-btn"
                  aria-label={`Unlock ${name} with Premium`}
                >
                  <Crown className="h-4 w-4 mr-2" aria-hidden="true" /> Unlock with Premium
                </Button>
                <Button
                  onClick={() => openPlans('compare')}
                  variant="outline"
                  className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10 h-11 px-6"
                  data-testid="tool-compare-plans-btn"
                  aria-label="Compare plans"
                >
                  Compare Plans
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <PlanSelectionModal open={open} onOpenChange={setOpen} />
    </>
  );
};

export default PremiumToolPreview;
